import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, IconButton, Paper, Typography, Fab, Snackbar, Alert, Divider, CircularProgress } from '@mui/material';
import { DragIndicator, Close, Note, Publish } from '@mui/icons-material';
import { getRequest, postRequest } from '../../Fetch/request';
import { useAuth } from '../../hooks/useAuth';
import './DraggableNotes.css';

export default function DraggableNotes({ problemId, problemType = 'LLD', problem }) {
  const { isAuthenticated } = useAuth();
  const storageKey = `${problemType.toLowerCase()}-notes-${problemId}`;
  const positionKey = `${problemType.toLowerCase()}-notes-position-${problemId}`;
  const sizeKey = `${problemType.toLowerCase()}-notes-size-${problemId}`;
  const visibilityKey = `${problemType.toLowerCase()}-notes-visible-${problemId}`;

  // Generate blogId from problem title and problemId
  const getBlogId = () => {
    if (!problem?.title || !problemId) return null;
    // Sanitize title for blogId (remove special chars, replace spaces with hyphens)
    const sanitizedTitle = problem.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `${sanitizedTitle}-${problemId}`;
  };

  // Load initial state from localStorage
  const getInitialPosition = () => {
    const saved = localStorage.getItem(positionKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { x: 20, y: 20 };
      }
    }
    return { x: 20, y: 20 };
  };

  const getInitialSize = () => {
    const saved = localStorage.getItem(sizeKey);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return { width: 350, height: 400 };
      }
    }
    return { width: 350, height: 400 };
  };

  const getInitialContent = () => {
    // Load from localStorage (includes learning points if added)
    const saved = localStorage.getItem(storageKey);
    return saved || '';
  };

  const getInitialVisibility = () => {
    const saved = localStorage.getItem(visibilityKey);
    return saved !== null ? JSON.parse(saved) : true;
  };

  const [position, setPosition] = useState(getInitialPosition);
  const [size, setSize] = useState(getInitialSize);
  const [content, setContent] = useState(getInitialContent);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(getInitialVisibility);
  const [publishedComponents, setPublishedComponents] = useState([]);
  const [isLoadingBlog, setIsLoadingBlog] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const boxRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const contentAreaRef = useRef(null);
  const textFieldRef = useRef(null);

  // Listen for learning points added event
  useEffect(() => {
    const handleLearningPointsAdded = (event) => {
      if (event.detail.problemId === problemId && event.detail.problemType === problemType) {
        // Reload content from localStorage to show new learning points
        const notesContent = localStorage.getItem(storageKey) || '';
        if (notesContent) {
          setContent(notesContent);
          // Scroll to the latest appended learning points
          requestAnimationFrame(() => {
            if (contentAreaRef.current) {
              contentAreaRef.current.scrollTop = contentAreaRef.current.scrollHeight;
            }
            if (textFieldRef.current) {
              textFieldRef.current.scrollTop = textFieldRef.current.scrollHeight;
            }
          });
        }
      }
    };

    window.addEventListener('learningPointsAdded', handleLearningPointsAdded);
    return () => {
      window.removeEventListener('learningPointsAdded', handleLearningPointsAdded);
    };
  }, [problemId, problemType, storageKey]);

  // Save content to localStorage whenever it changes (for draft, not published)
  useEffect(() => {
    if (content !== undefined && content.trim()) {
      localStorage.setItem(storageKey, content);
    }
  }, [content, storageKey]);

  // Save position to localStorage whenever it changes
  useEffect(() => {
    if (position.x !== undefined && position.y !== undefined) {
      localStorage.setItem(positionKey, JSON.stringify(position));
    }
  }, [position, positionKey]);

  // Save size to localStorage whenever it changes
  useEffect(() => {
    if (size.width !== undefined && size.height !== undefined) {
      localStorage.setItem(sizeKey, JSON.stringify(size));
    }
  }, [size, sizeKey]);

  // Save visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(visibilityKey, JSON.stringify(isVisible));
  }, [isVisible, visibilityKey]);

  // Fetch existing blog on mount and when problem changes
  useEffect(() => {
    const fetchExistingBlog = async () => {
      const blogId = getBlogId();
      if (!blogId || !problemId) return;

      setIsLoadingBlog(true);
      try {
        const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
        const response = await getRequest(`${process.env.REACT_APP_URL}/blog/getBlog?blogId=${blogId}&userId=${userId}`);

        if (response && response.status === '200' && response.response) {
          const blog = response.response;
          // Extract components from blog content
          const components = blog.content || [];
          setPublishedComponents(components);
          
          // Don't modify current content - published components will be displayed separately
        }
      } catch (error) {
        console.error('Error fetching blog:', error);
      } finally {
        setIsLoadingBlog(false);
      }
    };

    if (problem && problemId) {
      fetchExistingBlog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId, problem?.title]); // Re-fetch if problem title changes

  // Handle drag start - only from header
  const handleHeaderMouseDown = (e) => {
    if (e.target.closest('.resize-handle') || e.target.closest('button') || e.target.closest('.MuiIconButton-root')) {
      return; // Don't drag when clicking resize handle or buttons
    }
    setIsDragging(true);
    const rect = boxRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    e.preventDefault();
  };

  // Handle resize start
  const handleResizeMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle mouse move for both dragging and resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Keep within viewport bounds
        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      } else if (isResizing) {
        const rect = boxRef.current.getBoundingClientRect();
        const newWidth = e.clientX - rect.left;
        const newHeight = e.clientY - rect.top;
        
        // Minimum size constraints
        const minWidth = 200;
        const minHeight = 200;
        const maxWidth = window.innerWidth - position.x;
        const maxHeight = window.innerHeight - position.y;
        
        setSize({
          width: Math.max(minWidth, Math.min(newWidth, maxWidth)),
          height: Math.max(minHeight, Math.min(newHeight, maxHeight))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset, size, position]);

  // Handle publish
  const handlePublish = async () => {
    if (!content.trim()) {
      setSnackbar({
        open: true,
        message: 'Please write some notes before publishing',
        severity: 'warning'
      });
      return;
    }

    const blogId = getBlogId();
    if (!blogId) {
      setSnackbar({
        open: true,
        message: 'Cannot publish: Missing problem information',
        severity: 'error'
      });
      return;
    }

    setIsPublishing(true);
    try {
      const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
      
      // Create new component from current content
      const newComponent = {
        text: content.trim(),
        imageUrl: '',
        textAreaActive: true
      };

      // Fetch existing blog to get current components
      let existingComponents = [];
      try {
        const getResponse = await getRequest(`${process.env.REACT_APP_URL}/blog/getBlog?blogId=${blogId}&userId=${userId}`);
        if (getResponse && getResponse.status === '200' && getResponse.response) {
          existingComponents = getResponse.response.content || [];
        }
      } catch (error) {
        // Blog doesn't exist yet, will create new one
        console.log('Blog does not exist, creating new one');
      }

      // Append new component to existing components
      const updatedComponents = [...existingComponents, newComponent];

      // Create/update blog object
      const blogObject = {
        blogId: blogId,
        title: problem?.title || `Notes - ${problemId}`,
        author: userId,
        userId: userId,
        createdAt: Date.now(),
        content: updatedComponents,
        upVote: 0,
        downVote: 0
      };

      // Publish/update blog
      const postResponse = await postRequest(`${process.env.REACT_APP_URL}/blog/uploadBlog`, blogObject);

      if (postResponse && (postResponse.status === '200' || postResponse.status === 200 || postResponse.status === '201' || postResponse.status === 201)) {
        setPublishedComponents(updatedComponents);
        // Clear the textarea after publishing
        setContent('');
        // Clear localStorage draft
        localStorage.removeItem(storageKey);
        setSnackbar({
          open: true,
          message: 'Notes published successfully!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: postResponse?.message || 'Failed to publish notes',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error publishing notes:', error);
      setSnackbar({
        open: true,
        message: 'Error publishing notes. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // If not visible OR user is not authenticated, show a floating button to show it
  if (!isVisible || !isAuthenticated) {
    return (
      <>
        <Fab
          size="small"
          color="primary"
          onClick={() => {
            if (!isAuthenticated) {
              setSnackbar({ open: true, message: 'Please login to use Notes', severity: 'warning' });
              return;
            }
            setIsVisible(true);
          }}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 9999,
            backgroundColor: '#008080',
            '&:hover': {
              backgroundColor: '#006666'
            }
          }}
        >
          <Note />
        </Fab>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <>
      <Paper
        ref={boxRef}
        className="draggable-notes-container"
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${size.width}px`,
          height: `${size.height}px`,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
      >
        {/* Header with drag handle */}
        <Box
          className="notes-header"
          onMouseDown={handleHeaderMouseDown}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: '#008080',
            color: 'white',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
            flexShrink: 0
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DragIndicator sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Notes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={handlePublish}
              disabled={isPublishing || isLoadingBlog || !content.trim()}
              sx={{ 
                color: 'white', 
                padding: '4px',
                '&:disabled': { opacity: 0.5 }
              }}
              title="Publish notes"
            >
              {isPublishing ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : (
                <Publish sx={{ fontSize: 18 }} />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setIsVisible(false)}
              sx={{ color: 'white', padding: '4px' }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Content area */}
        <Box
          ref={contentAreaRef}
          sx={{
            flex: 1,
            padding: '8px',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#008080',
              borderRadius: '4px',
              '&:hover': {
                background: '#006666',
              },
            },
          }}
        >
          {isLoadingBlog ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {/* Editable textarea - new notes at the top */}
              <Box sx={{ flex: '0 0 auto', mb: 2, display: 'flex', flexDirection: 'column' }}>
                <TextField
                  multiline
                  fullWidth
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  inputRef={textFieldRef}
                  placeholder="Write your notes here..."
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      minHeight: '160px',
                      alignItems: 'flex-start',
                      fontSize: '14px'
                    },
                    '& .MuiInputBase-input': {
                      height: '100% !important',
                      overflow: 'auto !important',
                      wordWrap: 'break-word',
                      whiteSpace: 'pre-wrap',
                      boxSizing: 'border-box'
                    }
                  }}
                />
              </Box>

              {/* Published components - read-only in increasing order */}
              {publishedComponents.length > 0 && (
                <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  {publishedComponents.map((component, idx) => (
                    <Box key={`${component?.text || ''}-${idx}`} sx={{ mb: 2 }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          backgroundColor: '#f9f9f9',
                          borderLeft: '3px solid #008080'
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mb: 1,
                            color: '#666',
                            fontWeight: 'bold',
                            fontSize: '11px'
                          }}
                        >
                          Published Section {idx + 1}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            color: '#333',
                            fontSize: '13px',
                            lineHeight: 1.5
                          }}
                        >
                          {component.text || ''}
                        </Typography>
                      </Paper>
                      {idx < publishedComponents.length - 1 && (
                        <Divider sx={{ my: 1 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Resize handle - Professional diagonal lines pattern */}
        <Box
          ref={resizeHandleRef}
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '18px',
            height: '18px',
            cursor: 'nwse-resize',
            background: `
              linear-gradient(135deg, transparent 45%, #008080 45%, #008080 47%, transparent 47%),
              linear-gradient(135deg, transparent 48%, #008080 48%, #008080 50%, transparent 50%),
              linear-gradient(135deg, transparent 51%, #008080 51%, #008080 53%, transparent 53%)
            `,
            backgroundSize: '6px 6px',
            backgroundPosition: '0 0, 3px 3px, 6px 6px',
            opacity: 0.5,
            transition: 'opacity 0.2s',
            '&:hover': {
              opacity: 1,
              background: `
                linear-gradient(135deg, transparent 45%, #006666 45%, #006666 47%, transparent 47%),
                linear-gradient(135deg, transparent 48%, #006666 48%, #006666 50%, transparent 50%),
                linear-gradient(135deg, transparent 51%, #006666 51%, #006666 53%, transparent 53%)
              `,
              backgroundSize: '6px 6px',
              backgroundPosition: '0 0, 3px 3px, 6px 6px',
            }
          }}
        />
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
